import React, { Fragment } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { N20 } from '@atlaskit/theme/colors';

import { fg } from '@confluence/feature-gating';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types';
import {
	MacroExperienceSuccess,
	getMacroAttributesFromADFNode,
	getExperienceName,
} from '@confluence/macro-tracker';
import { CreateFromTemplateLoader as InteractableCreateFromTemplateButton } from '@confluence/create-from-template/entry-points/CreateFromTemplate';
import { useSessionData } from '@confluence/session-data';

const i18n = defineMessages({
	createFromTemplateDefaultLabelText: {
		id: 'fabric-extension-handlers.inline-extension.create-from-template',
		defaultMessage: 'Create from Template',
		description:
			'Button text that lets the user create a brand new templated page when they click on the button',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UninteractableCreateFromTemplateButton = styled.span({
	background: token('color.background.accent.gray.subtlest', N20),
	padding: `1px ${token('space.100', '8px')} ${token(
		'space.025',
		'2px',
	)} ${token('space.100', '8px')}`,
	borderRadius: '3px',
	font: token('font.body.large'),
});

export const CreateFromTemplate = ({ ...macroDefaultProps }: ExtensionHandlerProps) => {
	const { contentId, extensionKey, node, mode } = macroDefaultProps;
	const attributes = getMacroAttributesFromADFNode(node);
	const intl = useIntl();
	const { cloudId, userId } = useSessionData();

	const defaultText = intl.formatMessage(i18n.createFromTemplateDefaultLabelText);
	const buttonLabel = node?.parameters?.macroParams?.buttonLabel?.value || defaultText;

	return (
		<Fragment>
			{!fg('confluence_frontend_page_experiences_cft_wysiwig') ? (
				<UninteractableCreateFromTemplateButton data-testid="uninteractable-create-from-template-editor-button">
					<Text as="p">{buttonLabel}</Text>
				</UninteractableCreateFromTemplateButton>
			) : (
				<InteractableCreateFromTemplateButton
					userId={userId || ''}
					cloudId={cloudId}
					macroDefaultProps={macroDefaultProps}
				/>
			)}
			<MacroExperienceSuccess
				name={getExperienceName(mode, node)}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
		</Fragment>
	);
};
